"use client";

import { useAuth, useUser } from "@clerk/nextjs";
import { useIsSuperAdmin } from "@/hooks/use-user-role";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { SidebarProvider } from "@/components/ui/sidebar";
import posthog from "posthog-js";
import {
  captureGlobal,
  entryPathFromSearchParams,
  inferLoginMethod,
  registerAnalyticsGlobals,
} from "@/lib/analytics";
const AppSidebar = dynamic(
  () =>
    import("@/components/AppSidebar").then((mod) => ({
      default: mod.AppSidebar,
    })),
  {
    ssr: false,
  },
);

interface AuthWrapperProps {
  children: React.ReactNode;
}

export function AuthWrapper({ children }: AuthWrapperProps) {
  const { isLoaded, isSignedIn } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const identifiedRef = useRef(false);

  const publicRoutes = ["/sign-in", "/sign-up"];
  const isPublicRoute = publicRoutes.some((route) => pathname?.startsWith(route));
  const { superAdmin, isLoaded: roleLoaded } = useIsSuperAdmin();

  useEffect(() => {
    if (!isLoaded) return;

    // Identify the user in PostHog once when signed in
    if (isSignedIn && user && !identifiedRef.current) {
      identifiedRef.current = true;
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        first_name: user.firstName,
        last_name: user.lastName,
      });
    }

    if (isSignedIn && user) {
      const org = user.organizationMemberships?.[0]?.organization;
      registerAnalyticsGlobals({
        user_id: user.id,
        org_id: org?.id,
        workspace_id: org?.id,
        user_role: user.publicMetadata?.role ?? undefined,
        environment: process.env.NODE_ENV,
      });
    }

    if (isSignedIn && user && !isPublicRoute && typeof window !== "undefined") {
      if (!sessionStorage.getItem("lh_login_succeeded")) {
        sessionStorage.setItem("lh_login_succeeded", "1");
        const sp = new URLSearchParams(window.location.search);
        captureGlobal("login_succeeded", {
          login_method: inferLoginMethod(user),
          entry_path: entryPathFromSearchParams(sp),
        });
      }
    }

    if (!isSignedIn && typeof window !== "undefined") {
      sessionStorage.removeItem("lh_login_succeeded");
    }

    if (!isSignedIn && identifiedRef.current) {
      identifiedRef.current = false;
      posthog.reset();
    }

    // Redirect to sign-in if not authenticated and not on a public route
    if (!isSignedIn && !isPublicRoute) {
      router.push("/sign-in");
    }

    // Redirect after auth from sign-in/sign-up (respect Clerk redirect_url)
    if (isSignedIn && isPublicRoute && userLoaded) {
      if (typeof window !== "undefined") {
        const sp = new URLSearchParams(window.location.search);
        const redirectUrl = sp.get("redirect_url");
        if (redirectUrl) {
          return;
        }
      }
      router.push(roleLoaded && superAdmin ? "/admin" : "/");
    }

    // Role-based home vs admin dashboard (wait for role so super admins are not bounced off /admin)
    if (isSignedIn && user && userLoaded && roleLoaded && !isPublicRoute && pathname) {
      if (superAdmin && pathname === "/") {
        router.replace("/admin");
      }
      if (!superAdmin && pathname.startsWith("/admin")) {
        router.replace("/");
      }
    }
  }, [isLoaded, isSignedIn, isPublicRoute, router, pathname, user, userLoaded, roleLoaded, superAdmin]);

  // Show loading state while checking auth
  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth pages without sidebar
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Show protected content with sidebar
  if (isSignedIn) {
    return (
      <SidebarProvider defaultOpen={false}>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>
      </SidebarProvider>
    );
  }

  // Show loading while redirecting
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
        <p className="mt-4 text-sm text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
}

